import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "typeSelect", "articleFields", "bookFields", "commonFields",
    "name", "author", "journalName", "issn", "doi", 
    "publisher", "placeOfPublication", "edition", "isbn", "publicationDate"
  ];

  static values = {
    googleBooksApiKey: String
  };

  connect() {
    console.log("[Schobot] ReferenceForm controller connected");
    this.toggleFields();
    this.lastDoi = "";
    this.lastIsbn = "";
  }

  toggleFields() {
    const selectedType = this.typeSelectTarget.value;
    console.log("[Schobot] Selected reference type:", selectedType);

    // Hide all first
    this.articleFieldsTarget.classList.add("d-none");
    this.bookFieldsTarget.classList.add("d-none");
    this.commonFieldsTarget.classList.add("d-none");

    if (selectedType === "scientific_article") {
      this.articleFieldsTarget.classList.remove("d-none");
      this.commonFieldsTarget.classList.remove("d-none");
    } else if (selectedType === "book") {
      this.bookFieldsTarget.classList.remove("d-none");
      this.commonFieldsTarget.classList.remove("d-none");
    }
  }

  async handleDoiChange(event) {
    const doiInput = event.target;
    const doi = doiInput.value.trim();
    if (!doi || doi === this.lastDoi) return;
    this.lastDoi = doi;

    console.log("[Schobot] Triggered DOI lookup for:", doi);
    this.showLoading(doiInput);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(`https://api.crossref.org/works/${doi}`, { signal: controller.signal });
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        if (data.message) {
          const msg = data.message;
          
          // Extract attributes
          const title = msg.title ? msg.title[0] : "";
          const author = msg.author ? msg.author.map(a => `${a.given || ''} ${a.family || ''}`.trim()).join(", ") : "";
          const journal = msg['container-title'] ? msg['container-title'][0] : "";
          const publisher = msg.publisher || "";
          
          const published = msg.published || msg['created'];
          let publication_date = "";
          if (published && published['date-parts'] && published['date-parts'][0]) {
            publication_date = published['date-parts'][0].join('-');
          }
          const issn = msg.ISSN ? msg.ISSN[0] : "";

          // Autofill
          if (this.hasNameTarget && !this.nameTarget.value.trim()) this.nameTarget.value = title;
          if (this.hasAuthorTarget && !this.authorTarget.value.trim()) this.authorTarget.value = author;
          if (this.hasJournalNameTarget && !this.journalNameTarget.value.trim()) this.journalNameTarget.value = journal;
          if (this.hasIssnTarget && !this.issnTarget.value.trim()) this.issnTarget.value = issn;
          if (this.hasPublisherTarget && !this.publisherTarget.value.trim()) this.publisherTarget.value = publisher;
          if (this.hasPublicationDateTarget && !this.publicationDateTarget.value.trim()) this.publicationDateTarget.value = publication_date;

          console.log("[Schobot] DOI lookup autofill completed");
        }
      } else {
        console.warn("[Schobot] DOI lookup failed with status:", response.status);
      }
    } catch (e) {
      console.error("[Schobot] DOI lookup error:", e);
    } finally {
      this.hideLoading(doiInput);
    }
  }

  async handleIsbnChange(event) {
    const isbnInput = event.target;
    const rawIsbn = isbnInput.value.trim();
    const isbn = rawIsbn.replace(/[- ]/g, "");
    if (!isbn || isbn === this.lastIsbn) return;
    
    if (isbn.length !== 10 && isbn.length !== 13) {
      console.warn("[Schobot] Invalid ISBN length:", isbn.length);
      return;
    }
    this.lastIsbn = isbn;

    console.log("[Schobot] Triggered ISBN lookup for:", isbn);
    this.showLoading(isbnInput);

    let found = false;

    // 1. Try Google Books API if key is present
    if (this.hasGoogleBooksApiKeyValue && this.googleBooksApiKeyValue.trim()) {
      console.log("[Schobot] Trying Google Books with API key...");
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const key = this.googleBooksApiKeyValue.trim();
        const response = await fetch(`https://www.googleapis.com/books/v1/volumes?q=isbn:${isbn}&key=${key}`, { signal: controller.signal });
        clearTimeout(timeoutId);

        if (response.ok) {
          const data = await response.json();
          if (data.items && data.items.length > 0) {
            const info = data.items[0].volumeInfo;
            const title = info.title || "";
            const author = info.authors ? info.authors.join(", ") : "";
            const publisher = info.publisher || "";
            const published = info.publishedDate || "";

            // Autofill
            if (this.hasNameTarget && !this.nameTarget.value.trim()) this.nameTarget.value = title;
            if (this.hasAuthorTarget && !this.authorTarget.value.trim()) this.authorTarget.value = author;
            if (this.hasPublisherTarget && !this.publisherTarget.value.trim()) this.publisherTarget.value = publisher;
            if (this.hasPublicationDateTarget && !this.publicationDateTarget.value.trim()) this.publicationDateTarget.value = published;

            console.log("[Schobot] Google Books lookup autofill completed");
            found = true;
          }
        } else {
          console.warn("[Schobot] Google Books lookup failed with status:", response.status);
        }
      } catch (e) {
        console.error("[Schobot] Google Books lookup error:", e);
      }
    } else {
      console.warn("[Schobot] No Google Books API Key configured.");
    }

    // 2. Fallback to Open Library
    if (!found) {
      console.log("[Schobot] Falling back to Open Library lookup...");
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const bibkey = `ISBN:${isbn}`;
        const response = await fetch(`https://openlibrary.org/api/books?bibkeys=${bibkey}&format=json&jscmd=data`, { signal: controller.signal });
        clearTimeout(timeoutId);

        if (response.ok) {
          const data = await response.json();
          if (data[bibkey]) {
            const info = data[bibkey];
            
            const title = info.title || "";
            const author = info.authors ? info.authors.map(a => a.name).join(", ") : "";
            const publisher = info.publishers ? info.publishers.map(p => p.name).join(", ") : "";
            const published = info.publish_date || "";
            const place = info.publish_places ? info.publish_places.map(p => p.name).join(", ") : "";
            const edition = info.edition_name || "";

            // Autofill
            if (this.hasNameTarget && !this.nameTarget.value.trim()) this.nameTarget.value = title;
            if (this.hasAuthorTarget && !this.authorTarget.value.trim()) this.authorTarget.value = author;
            if (this.hasPublisherTarget && !this.publisherTarget.value.trim()) this.publisherTarget.value = publisher;
            if (this.hasPublicationDateTarget && !this.publicationDateTarget.value.trim()) this.publicationDateTarget.value = published;
            if (this.hasPlaceOfPublicationTarget && !this.placeOfPublicationTarget.value.trim()) this.placeOfPublicationTarget.value = place;
            if (this.hasEditionTarget && !this.editionTarget.value.trim()) this.editionTarget.value = edition;

            console.log("[Schobot] Open Library lookup autofill completed");
            found = true;
          } else {
            console.warn("[Schobot] ISBN not found in Open Library database");
          }
        } else {
          console.warn("[Schobot] Open Library lookup failed with status:", response.status);
        }
      } catch (e) {
        console.error("[Schobot] Open Library lookup error:", e);
      }
    }

    this.hideLoading(isbnInput);
  }

  showLoading(inputEl) {
    this.hideLoading(inputEl);
    const spinner = document.createElement("span");
    spinner.className = "ref-form-spinner badge bg-info text-dark ms-2";
    spinner.style.fontSize = "0.8rem";
    spinner.innerText = "Recherche en cours...";
    
    if (inputEl.parentNode) {
      inputEl.parentNode.appendChild(spinner);
    }
  }

  hideLoading(inputEl) {
    if (inputEl.parentNode) {
      const spinner = inputEl.parentNode.querySelector(".ref-form-spinner");
      if (spinner) {
        spinner.remove();
      }
    }
  }
}
